import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import logger from "../../utils/logger";

// Centralized Error Handling Types
export interface StandUpError {
  id: string;
  apiName: string;
  message: string;
  timestamp: number;
  context?: any;
}

export interface CentralizedErrorState {
  errors: StandUpError[];
  activeError: StandUpError | null;
  isErrorModalVisible: boolean;
}

// API Names for consistent error tracking
export const STANDUP_API_NAMES = {
  FETCH_STANDUP_DATA: 'fetchStandUpData',
  FETCH_TOP_OPPORTUNITIES: 'fetchTopOpportunities',
  FETCH_NEWS: 'fetchNews',
  FETCH_NEWS_DETAILS: 'fetchNewsDetails',
  ADD_NEWS: 'addNews',
  DELETE_NEWS: 'deleteNews',
  FETCH_FAVORITES: 'fetchFavorites',
  FETCH_LIGHTHOUSE_EXECUTION: 'fetchLighthouseExecution',
  FETCH_METRIC_TREND: 'fetchMetricTrend',
  FETCH_KPI_METRICS: 'fetchKpiMetrics',
  FETCH_LIGHTHOUSE_TASKS: 'fetchLighthouseTasks',
  ADD_FAVORITE: 'addFavorite',
  UPDATE_FAVORITE: 'updateFavorite',
  REMOVE_FAVORITE: 'removeFavorite',
  SUBMIT_STANDUP: 'submitStandUp',
  UPDATE_STANDUP: 'updateStandup',
  CREATE_ACTION_PLAN: 'createActionPlan',
  UPDATE_ACTION_PLAN: 'updateActionPlan',
  FETCH_ACTION_PLAN: 'fetchActionPlan',
  ACCEPT_ACTION_PLAN: 'acceptActionPlan',
  DECLINE_ACTION_PLAN: 'declineActionPlan',
  COMPLETE_ACTION_PLAN: 'completeActionPlan',
  DELETE_ACTION_PLAN: 'deleteActionPlan',
  TOGGLE_FAVORITE_OPPORTUNITY: 'toggleFavoriteOpportunity',
  DISCARD_STANDUP: 'discardStandup',
  UPDATE_ACTION_PLAN_TOP_OPPORTUNITY: 'updateActionPlanTopOpportunity',
  DOWNLOAD_ACTION_PLAN_PDF: 'downloadActionPlanPDF',
} as const;

export interface StandUp {
  id: number;
  title: string;
  description: string;
  standup_type: string; // e.g., 'daily_flt'
  scheduled_date: string; // "2026-01-06T00:00:00.000Z"
  status: string; // e.g., "Saved"
  created_at: string;
  modified_at: string;
  period: string;
  week: string;
  year: string;
  location_id: number;
  location_name: string;
  created_by: string;
  modified_by: string;
}

export interface TopOpportunity {
  ID: number;
  TITLE: string;
  SOURCE: string;
  TYPE: string;
  PRIORITY: string;
  STATUS: string;
  OWNER_GPID: string;
  OWNER_ROLE?: string;
  ASSIGNEE_GPID: string;
  OWNER_NAME?: string;
  ASSIGNEE_NAME?: string;
  OWNER_EMAIL?: string;
  ASSIGNEE_EMAIL?: string;
  TARGET_DATE?: string;
  START_DATE?: string;
  CREATED_AT?: string;
  UPDATED_AT?: string;
  DESCRIPTION?: string;
  INVOLVED_EMPLOYEE_GPIDS?: string;
  INVOLVED_EMPLOYEE_NAMES?: string;
  ACTION_PLAN_DESCRIPTION?: string;
  LOCATION?: string;
  [key: string]: any;
}

export interface NewsItem {
  id: string;
  title: string;
  description: string;
  date: string;
  createdBy: string;
  created_at?: string;
  is_truncated?: boolean;
}

export interface StandupState {
  standUps: StandUp[];
  topOpportunities: TopOpportunity[];
  news: NewsItem[];
  loading: boolean;
  error: string | null;
  toastNotification: ToastNotification | null;
  lastFetchedLocation: string | null; // Track when data was fetched
  successMessage: string | null;
  standupStatus: "In Progress" | "Saved" | "Not Started" | ''; // Store standup status
  isSubmittedBtnClicked: boolean; // Track if submit button was clicked
  isSavedForLater: boolean; // Track if save for later button was clicked
  isSubmitted: boolean; // Track if submit button was clicked
  centralizedErrors: CentralizedErrorState; // Centralized error handling
}

export interface ToastNotification {
  header: string;
  subtext: string;
  variant: "success" | "error" | "warning" | "info";
  position?:
  | "top"
  | "bottom"
  | "top-right"
  | "top-left"
  | "bottom-right"
  | "bottom-left";
  duration?: number;
}

const initialState: StandupState = {
  standUps: [],
  topOpportunities: [],
  news: [],
  loading: false,
  error: null,
  toastNotification: null,
  lastFetchedLocation: null,
  successMessage: null,
  standupStatus: 'In Progress',
  isSubmittedBtnClicked: false,
  isSavedForLater: false,
  isSubmitted: false,
  centralizedErrors: {
    errors: [],
    activeError: null,
    isErrorModalVisible: false,
  },
};


/** -------------------- Slice -------------------- */
const standUpSlice = createSlice({
  name: "standUp",
  initialState,
  reducers: {
    // Centralized Error Handling Actions
    addCentralizedError(state, action: PayloadAction<StandUpError>) {
      state.centralizedErrors.errors.push(action.payload);
      // Keep only the last 10 errors to prevent memory issues
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },
    setActiveError(state, action: PayloadAction<StandUpError | null>) {
      state.centralizedErrors.activeError = action.payload;
    },
    showErrorModal(state, action: PayloadAction<StandUpError>) {
      state.centralizedErrors.activeError = action.payload;
      state.centralizedErrors.isErrorModalVisible = true;
    },
    hideErrorModal(state) {
      state.centralizedErrors.isErrorModalVisible = false;
      state.centralizedErrors.activeError = null;
    },
    clearAllErrors(state) {
      state.centralizedErrors.errors = [];
      state.centralizedErrors.activeError = null;
      state.centralizedErrors.isErrorModalVisible = false;
    },
    removeErrorById(state, action: PayloadAction<string>) {
      state.centralizedErrors.errors = state.centralizedErrors.errors.filter(
        error => error.id !== action.payload
      );
      // If the active error was removed, hide the modal
      if (state.centralizedErrors.activeError?.id === action.payload) {
        state.centralizedErrors.activeError = null;
        state.centralizedErrors.isErrorModalVisible = false;
      }
    },

    // Set all standUps
    setStandUps: (state, action: PayloadAction<StandUp[]>) => {
      state.standUps = action?.payload;
      state.error = null;
    },

    // Set top opportunities
    setTopOpportunities: (state, action: PayloadAction<TopOpportunity[]>) => {
      console.log("🟢 REDUCER: setTopOpportunities called with payload length:", action?.payload?.length);
      console.log("🟢 REDUCER: Payload preview:", action?.payload?.slice(0, 1));
      state.topOpportunities = action?.payload;
      console.log("🟢 REDUCER: State updated, new length:", state.topOpportunities.length);
      state.error = null;
    },

    // Clear top opportunities
    clearTopOpportunities: (state) => {
      state.topOpportunities = [];
    },
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },

    // Set news data
    setNews: (state, action: PayloadAction<NewsItem[]>) => {
      state.news = action?.payload;
      state.error = null;
    },

    // Clear news
    clearNews: (state) => {
      state.news = [];
    },

    // Set last fetched location (to track when to refresh data)
    setLastFetchedLocation: (state, action: PayloadAction<string>) => {
      state.lastFetchedLocation = action.payload;
    },

    /** CRUD */
    addStandup: (state, action: PayloadAction<StandUp>) => {
      state.standUps.push(action.payload);
      state.error = null;
    },

    updateStandup: (state, action: PayloadAction<StandUp>) => {
      const idx = state.standUps.findIndex(
        (s) => s.id === action.payload.id
      );
      if (idx !== -1) {
        state.standUps[idx] = action.payload;
      }
      state.error = null;
    },

    deleteStandup: (state, action: PayloadAction<number>) => {
      state.standUps = state.standUps.filter(
        (s) => s.id !== action.payload
      );
      state.error = null;
    },

    /** UI flags */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },

    /** Toast helpers (same pattern as reference) */
    setToastNotification: (
      state,
      action: PayloadAction<ToastNotification>
    ) => {
      logger.log("🟢 REDUCER: setToastNotification called with payload:", action.payload);
      state.toastNotification = action.payload;
    },

    clearToastNotification: (state) => {
      state.toastNotification = null;
    },

    checkIsSubmittedBtnClick: (state, action: PayloadAction<boolean>) => {
      state.isSubmittedBtnClicked = action.payload;
    },
    setIsSavedForLater: (state, action: PayloadAction<boolean>) => {
      state.isSavedForLater = action.payload;
    },
    setIsSubmitted: (state, action: PayloadAction<boolean>) => {
      state.isSubmitted = action.payload;
    },
    /** Date comparison with today - Return status based on date and action type */
    setStandupStatus: (state, action: PayloadAction<string | { isSaveForLater: boolean; date: string }>) => {
      try {
        let dateString: string;
        let isSaveForLater: boolean = false;

        // Handle both string and object payloads
        if (typeof action.payload === 'string') {
          dateString = action.payload;
        } else {
          dateString = action.payload.date;
          isSaveForLater = action.payload.isSaveForLater;
        }

        if (!dateString) {
          state.standupStatus = "Not Started";
          return;
        }

        // Parse the date string and create UTC date
        let year: number, month: number, day: number;

        // Handle ISO format (2026-01-06T00:00:00.000Z or 2026-01-06)
        if (dateString.includes('T') || dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
          const parts = dateString.split('T')[0].split('-');
          year = parseInt(parts[0], 10);
          month = parseInt(parts[1], 10);
          day = parseInt(parts[2], 10);
        }
        // Handle DD/MM/YYYY format
        else if (dateString.includes('/')) {
          const parts = dateString.split('/');
          if (parts.length === 3) {
            day = parseInt(parts[0], 10);
            month = parseInt(parts[1], 10);
            year = parseInt(parts[2], 10);
          } else {
            state.standupStatus = "Not Started";
            return;
          }
        } else {
          state.standupStatus = "Not Started";
          return;
        }

        // Validate parsed values
        if (isNaN(year) || isNaN(month) || isNaN(day)) {
          state.standupStatus = "Not Started";
          return;
        }

        // Create dates in UTC using Date.UTC() for proper timezone-agnostic comparison
        const givenDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
        const today = new Date();
        const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0));

        // Compare timestamps
        const todayTime = todayUTC.getTime();
        const givenTime = givenDate.getTime();

        logger.log(`Date comparison - Given: ${givenDate.toISOString()}, Today: ${todayUTC.toISOString()}, isSaveForLater: ${isSaveForLater}`);

        // Logic:
        // - "In Progress" when date = today
        // - "Saved" when date != today AND user clicked "Save for Later"
        // - "Not Started" when date != today AND user clicked "Submit" but didn't complete
        if (givenTime === todayTime) {
          state.standupStatus = "In Progress";
        } else if (givenTime !== todayTime) {
          // Date is not today
          if (isSaveForLater) {
            state.standupStatus = "Saved";
          } else {
            state.standupStatus = "Not Started";
          }
        }
      } catch (error) {
        logger.error("Error comparing dates:", error);
        state.standupStatus = "Not Started";
      }
    },
  },
});

export const {
  // Centralized error handling
  addCentralizedError,
  setActiveError,
  showErrorModal,
  hideErrorModal,
  clearAllErrors,
  removeErrorById,
  // Existing exports
  addStandup,
  updateStandup,
  deleteStandup,
  setLoading,
  setError,
  setToastNotification,
  clearToastNotification,
  setStandUps,
  setTopOpportunities,
  clearTopOpportunities,
  setSuccessMessage,
  clearSuccessMessage,
  setNews,
  clearNews,
  setLastFetchedLocation,
  setStandupStatus,
  checkIsSubmittedBtnClick,
  setIsSavedForLater,
  setIsSubmitted,
} = standUpSlice.actions;

export default standUpSlice.reducer;
